import { Component, inject, ChangeDetectionStrategy, computed } from '@angular/core';

import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';

import { selectValidationDisplayConfig } from 'src/app/store/pages/quote/validation/validation.selectors';
import { selectIsDeveloper } from 'src/app/store/user/user.selectors';
import { selectQuoteState } from 'src/app/store/pages/quote/quote.selectors';
import { QuoteActions } from 'src/app/store/pages/quote/quote.actions';
import { ValidationErrorsDialogComponent } from './dialogs/validation-errors-dialog/validation-errors-dialog.component';
import { LocaleService } from 'src/app/shared/util/locale.service';

@Component({
    selector: 'app-quote-error-actions',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TooltipModule, TranslateModule],
    templateUrl: './quote-error-actions.component.html',
    styleUrl: './quote-error-actions.component.scss'
})
export class QuoteErrorActionsComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);
    private readonly dialogService = inject(DialogService);
    private readonly locale = inject(LocaleService);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly displayConfig = toSignal(this.store.select(selectValidationDisplayConfig));
    public readonly isDeveloper = toSignal(this.store.select(selectIsDeveloper), { initialValue: false });
    private readonly quote = toSignal(this.store.select(selectQuoteState));

    // =========================================================================
    // COMPUTED - Tooltips
    // =========================================================================
    public readonly errorsTooltip = computed(() => {
        this.locale.currentLang();
        const count = this.displayConfig()?.errorCount ?? 0;
        const text = this.translate.instant('QUOTE.TOOLBAR.VALIDATION_ERRORS', { count });
        const clickHint = this.translate.instant('QUOTE.TOOLBAR.CLICK_FOR_DETAILS');
        return `${text}\n${clickHint}`;
    });

    public readonly warningsTooltip = computed(() => {
        this.locale.currentLang();
        const count = this.displayConfig()?.warningCount ?? 0;
        const text = this.translate.instant('QUOTE.TOOLBAR.VALIDATION_WARNINGS', { count });
        const clickHint = this.translate.instant('QUOTE.TOOLBAR.CLICK_FOR_DETAILS');
        return `${text}\n${clickHint}`;
    });

    public readonly combinedTooltip = computed(() => {
        this.locale.currentLang();
        const parts: string[] = [];
        const errorCount = this.displayConfig()?.errorCount ?? 0;
        const warningCount = this.displayConfig()?.warningCount ?? 0;
        if (errorCount > 0) {
            parts.push(this.translate.instant('QUOTE.TOOLBAR.VALIDATION_ERRORS', { count: errorCount }));
        }
        if (warningCount > 0) {
            parts.push(this.translate.instant('QUOTE.TOOLBAR.VALIDATION_WARNINGS', { count: warningCount }));
        }
        const clickHint = this.translate.instant('QUOTE.TOOLBAR.CLICK_FOR_DETAILS');
        return [...parts, clickHint].join('\n');
    });

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public openValidationErrorsDialog(): void {
        this.dialogService.open(ValidationErrorsDialogComponent, {
            header: this.translate.instant('QUOTE.TOOLBAR.VALIDATION_ERRORS_TITLE'),
            closable: true,
            styleClass: 'dialog-container',
            draggable: false,
            dismissableMask: true,
        });
    }

    public onRefreshQuote(): void {
        const id = this.quote()?.id;
        if (id) {
            this.store.dispatch(QuoteActions.initializeQuote({ quoteId: id }));
        }
    }
}
